import { GuestLiteModel } from 'api-hooks/guest';
import { HotelLiteModel } from 'api-hooks/hotel';
import { Expose, Type } from 'class-transformer';
import { HotelCheckInStatusEnum } from 'common/constants/enum';
import {
  CommonModel,
  CommonParamInput,
} from 'common/repositories/common.model';

import { CommonMutationInput, SaleLiteModel, SaleModel } from '../common';

// Model

export class HotelSaleAddOnsItemModel {
  id: string;
  description: string;

  @Type(() => Number)
  price: number;

  @Type(() => GuestLiteModel)
  guest: GuestLiteModel;

  @Type(() => Number)
  unit?: number;

  @Expose({ name: 'price_unit' })
  @Type(() => Number)
  priceUnit?: number;
}

export class HotelSaleHotelRoomModel {
  id: string;
  type: string;
  size: string;
  description: string;
}

export class HotelSaleHotelItemModel extends CommonModel {
  detail?: string | null; // Other word: Meals

  @Expose({ name: 'hotel_confirmation_code' })
  hotelConfirmationCode: string | null;

  @Expose({ name: 'check_in_status' })
  checkInStatus: HotelCheckInStatusEnum | null;

  @Expose({ name: 'hotel_room' })
  @Type(() => HotelSaleHotelRoomModel)
  hotelRoom: HotelSaleHotelRoomModel;

  @Expose({ name: 'check_in' })
  @Type(() => Date)
  checkIn: Date;

  @Expose({ name: 'check_out' })
  @Type(() => Date)
  checkOut: Date;

  @Type(() => Number)
  night: number;

  @Type(() => Number)
  room: number;

  @Type(() => Number)
  price: number;

  @Type(() => Number)
  pax?: number | null;
}

export class HotelSaleGuestItemModel extends CommonModel {
  preferences: { id: string; name: string }[];

  @Expose({ name: 'is_lead' })
  @Type(() => Boolean)
  isLead: boolean;

  @Type(() => GuestLiteModel)
  guest: GuestLiteModel;
}

export class HotelSaleHotelSaleItemModel extends CommonModel {
  remark?: string; // deprecated

  @Expose({ name: 'reschedule_fee' })
  @Type(() => Number)
  rescheduleFee?: number | null;

  @Expose({ name: 'booking_code' })
  bookingCode?: string | null;

  @Type(() => HotelLiteModel)
  hotel: HotelLiteModel;

  @Expose({ name: 'is_city_tax' })
  @Type(() => Boolean)
  isCityTax: boolean;

  @Expose({ name: 'is_by_pax' })
  @Type(() => Boolean)
  isByPax: boolean;

  @Expose({ name: 'city_tax' })
  cityTax: string | null;

  @Expose({ name: 'hotel_sale_guests' })
  @Type(() => HotelSaleGuestItemModel)
  hotelSaleGuests: HotelSaleGuestItemModel[];

  @Expose({ name: 'hotel_sale_hotels' })
  @Type(() => HotelSaleHotelItemModel)
  hotelSaleHotels: HotelSaleHotelItemModel[];

  @Expose({ name: 'hotel_sale_add_ons' })
  @Type(() => HotelSaleAddOnsItemModel)
  hotelSaleAddOns: HotelSaleAddOnsItemModel[];
}

export class HotelSalePreferenceItemModel extends CommonModel {
  description: string;

  @Expose({ name: 'hotel_sale_id' })
  hotelSaleId: string;

  @Expose({ name: 'preference_items' })
  preferenceItems: { id: string; label: string }[];

  @Expose({ name: 'hotel_sale_guest' })
  @Type(() => HotelSaleGuestItemModel)
  hotelSaleGuest: HotelSaleGuestItemModel;

  @Expose({ name: 'hotel_sale_hotel' })
  @Type(() => HotelSaleHotelItemModel)
  hotelSaleHotel: HotelSaleHotelItemModel;
}

export class HotelSaleLiteModel extends SaleLiteModel {}

export class HotelSaleModel extends SaleModel {
  @Expose({ name: 'hotel_sales' })
  @Type(() => HotelSaleHotelSaleItemModel)
  hotelSales: HotelSaleHotelSaleItemModel[];
}

// Input

export class getHotelSalesInput {
  params: CommonParamInput & {
    selection?: string;
  };
}

export class getHotelSaleInput {
  id: string;
}

export class HotelSaleGuestItemInput {
  id?: string | null;
  guestId: string | null;
  preferences?: string[];
  isLead: boolean;
}

export class HotelSaleHotelItemInput {
  id?: string | null;
  checkIn: Date | null;
  checkOut: Date | null;
  detail?: string | null;
  night: number;
  room: number;
  price: number;
  hotelRoomId: string | null;
  pax?: number | null;
}

export class HotelSaleAddOnsItemInput {
  id?: string | null;
  guestId: string | null;
  description: string;
  price: number;
  priceUnit?: number;
  unit?: number;
}

export class HotelSaleHotelSalesItemInput {
  id?: string | null;
  hotelId: string | null;
  bookingCode?: string | null;
  hotelConfirmation?: string | null;
  remark?: string;
  isCityTax: boolean;
  cityTax: string | null;
  hotelSaleGuests: HotelSaleGuestItemInput[];
  hotelSaleHotels: HotelSaleHotelItemInput[];
  hotelSaleAddOns: HotelSaleAddOnsItemInput[];
}

export class HotelSaleMutationInput extends CommonMutationInput {
  hotelSales: HotelSaleHotelSalesItemInput[];
}

export class HotelSaleRescheduleItemInput extends HotelSaleHotelSalesItemInput {
  rescheduleFee: number;
}

export class HotelSaleRescheduleDetailGuestMutationInput {
  isLead: boolean;
  preferences: string[];
  id: string;
  guestId: string;
}
export class HotelSaleRescheduleDetailHotelMutationInput {
  checkIn: Date | null;
  checkOut: Date | null;
  detail: string;
  pax: number;
  night: number;
  room: number;
  price: number;
  checkInStatus: string | null;
  hotelConfirmationCode: string;
  id: string;
  hotelRoomId: string;
}
export class HotelSaleRescheduleDetailAddOnsMutationInput {
  guestId: string;
  description: string;
  price: number;
  priceUnit: number;
  unit: number;
}

export class HotelSaleRescheduleDetailMutationInput {
  id: string;
  hotelId: string;
  bookingCode: string;
  isCityTax: boolean;
  isByPax: boolean;
  cityTax: string;
  hotelSaleGuests: HotelSaleRescheduleDetailGuestMutationInput[];
  hotelSaleHotels: HotelSaleRescheduleDetailHotelMutationInput[];
  hotelSaleAddOns: HotelSaleRescheduleDetailAddOnsMutationInput[];
}
export class HotelSaleRescheduleMutationInput {
  transactionAt: Date;
  total: number;
  hotelSales: HotelSaleRescheduleDetailMutationInput[];
}

export class HotelSaleReschedulePutInput {
  hotelSaleId: string;
  body: HotelSaleRescheduleMutationInput;
}

export class HotelSaleUpdateInput {
  hotelSaleId: string;
  body: HotelSaleMutationInput;
}

export class HotelSaleDeleteInput {
  id: string;
}
